import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
} from '../../../components/CommandControlComponents'

const Incidents = () => (
  <Page
    title="Incidents"
    intro="Incidents are live operational records. They can be started quickly, dispatched while information is still being gathered, and updated throughout the event."
  >
    <Section title="Starting an incident">
      <Bullets>
        <Bullet>
          Use New Incident from the action bar or the add button on incident
          modules.
        </Bullet>
        <Bullet>
          An incident appears immediately in the control room as soon as it is
          started.
        </Bullet>
        <Bullet>
          The form records incident type, presenting complaint, priority, caller
          details, location, description, and map coordinates.
        </Bullet>
        <Bullet>
          The location map defaults to the control room's current map type, so
          operators can place the incident on Map or Event map without switching
          context.
        </Bullet>
      </Bullets>
      <Info>
        Because new incidents appear before every field is complete, resources
        can be assigned while call-taking continues. Creating and updating
        incidents requires Dispatcher or Call Taker access; Viewers can open
        and read incidents but cannot change them.
      </Info>
    </Section>

    <Section title="Call triage">
      <Bullets>
        <Bullet>
          The incident form includes structured call triage: pick a presenting
          complaint from your organisation's approved triage list, then answer
          its questions while on the call.
        </Bullet>
        <Bullet>
          Answers calculate a suggested priority live, along with any warnings
          triggered by the answers.
        </Bullet>
        <Bullet>
          The call taker can accept the suggested priority or override it — an
          override requires a reason, and the incident shows an override
          marker with that reason.
        </Bullet>
        <Bullet>
          The triage summary stays on the incident and can be edited later by
          a dispatcher, for example when more information arrives.
        </Bullet>
      </Bullets>
      <Info>
        The triage complaint library itself (questions, rules, and default
        priorities) is configured and approved in Governance settings — the
        control room only ever offers approved complaints.
      </Info>
    </Section>

    <Section title="Dispatch List and Ongoing Incidents">
      <Bullets>
        <Bullet>Dispatch List contains only new incidents.</Bullet>
        <Bullet>
          Ongoing Incidents contains all non-new active incidents, including
          incidents without an assigned resource.
        </Bullet>
        <Bullet>
          Ongoing Incidents are sorted with Critical calls first, then calls
          without an active resource, then the normal priority/time order.
        </Bullet>
        <Bullet>
          Incidents without an active resource pulse so they remain visible
          after leaving the Dispatch List.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Incident cards">
      <Bullets>
        <Bullet>
          Cards show the incident number, type, priority, presenting complaint,
          location, and assigned resources where relevant.
        </Bullet>
        <Bullet>
          Dispatch cards do not show resource metadata because dispatch-list
          calls should not have an active resource yet.
        </Bullet>
        <Bullet>
          Ongoing cards show resource and location metadata along the bottom and
          can show a short description beneath the presenting complaint.
        </Bullet>
      </Bullets>
      <Screenshot url="command-control/dispatch-list.png">
        Dispatch List and Ongoing Incidents cards showing priority chips,
        no-resource state, and compact ongoing metadata.
      </Screenshot>
    </Section>

    <Section title="Editing and location protection">
      <Bullets>
        <Bullet>
          Opening an incident shows the map again so the incident location can
          be checked or changed.
        </Bullet>
        <Bullet>
          Existing incident locations are protected from accidental map clicks.
        </Bullet>
        <Bullet>
          Manual event-map repositioning opens in a larger popup so operators
          can zoom and pan before clicking once to set the location.
        </Bullet>
        <Bullet>
          Changing incident coordinates creates an incident log entry.
        </Bullet>
        <Bullet>
          Crew chat sent while a resource is assigned to the incident also
          appears in the incident log, marked as a chat message.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Dispatch alerts and crew acknowledgement">
      <Bullets>
        <Bullet>
          Assigning a resource sounds a full-screen alarm on the crew's phones
          asking them to acknowledge the tasking.
        </Bullet>
        <Bullet>
          Until the crew acknowledges, the resource status flashes as
          Assigned; acknowledging moves it to Acknowledged so the control room
          can see the crew has the job.
        </Bullet>
        <Bullet>
          Unacknowledged alerts are re-sent roughly every 35 seconds for up to
          15 minutes, so a crew member who stepped away still gets the call.
        </Bullet>
        <Bullet>
          Standing a resource down — clearing it, reassigning it, or moving it
          to another room — sends the crew a stand-down alert, which they also
          acknowledge. The control room plays a confirmation tone when a
          stand-down is acknowledged.
        </Bullet>
        <Bullet>
          Crew acknowledgements work offline: the app records the
          acknowledgement locally, silences further alerts on that device, and
          sends it to the server as soon as connectivity returns.
        </Bullet>
      </Bullets>
      <Info>
        On iPhones the app can only silence repeat alert banners while it is
        running. If a crew member's phone is offline with the app closed, a
        repeated banner may still appear until the acknowledgement reaches the
        server.
      </Info>
    </Section>

    <Section title="All incidents popup">
      <Bullets>
        <Bullet>
          Use the search popup to review all incidents in the control room.
        </Bullet>
        <Bullet>The search bar can find an incident by incident number.</Bullet>
        <Bullet>
          Selecting an incident opens it in the same detail drawer used by the
          active incident list.
        </Bullet>
      </Bullets>
    </Section>
  </Page>
)

export default Incidents
